import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./CipherPaywall.module.scss"

/**
 * CipherPaywall (`/cipher`) — an original, self-contained NON-ONBOARDING paywall
 * for Private Photo Vault Premium. Paywall type rolled at random for this run:
 * NON-ONBOARDING (a returning-user upgrade screen, not a first-run setup).
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot message this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it: the pitch is SECURITY &
 * PRIVACY through ENCRYPTION — the promise that even a stolen phone, a copied
 * file, or a lifted backup yields nothing readable to anyone but you.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * The strongest privacy isn't a locked door — it's a room that, to anyone
 * without the key, contains nothing but static. Your vault stores every photo
 * ENCRYPTED. On screen your library sits as a wall of cipher noise; you drag
 * YOUR KEY across it and a bright decryption seam sweeps left-to-right, turning
 * the noise back into your actual memories column by column. Let go and it never
 * re-scrambles what you've opened — because you hold the key. The pitch is
 * contrast + risk reversal: "To everyone else it's noise. To you it's
 * everything." What a thief gets vs. what you get.
 *
 * ── Divergence (vs. the 80+ prior concepts, incl. this run's siblings) ──────
 * Layout      : a full-bleed DECRYPTION CONSOLE — a grid of your photos rendered
 *               as cipher static, with a physical KEY slider that drives a
 *               sweeping seam. Not a hero+bullets card; not Imprint's centred
 *               biometric sensor; not PlainSight's home-screen springboard.
 * Interaction : DRAG-THE-KEY decryption seam — a horizontal scrub that resolves
 *               noise → photo column by column as it passes. New to the set:
 *               not press-to-enrol (Imprint), not tap-to-disguise (PlainSight),
 *               not hold (Kept) — a continuous positional wipe you steer.
 * Metaphor    : ENCRYPTION AS NOISE — memories stored as static that only your
 *               key turns legible. A genuinely fresh security visual for the set.
 * Value frame : CONTRAST + RISK REVERSAL — "what a thief sees vs. what you see."
 *               Not identity/commitment (Imprint), not concealment (PlainSight).
 * CTA frame   : "keep the key, keep them yours" — ownership of the key, not a
 *               countdown or a threat.
 * Register    : calm technical confidence — reassurance about a breach, not fear.
 *               Deep-navy "secure data" canvas + pure brand-blue seam and a
 *               monospace cipher motif; distinct from Imprint's obsidian-teal.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Encryption, the only-you lock (Face ID / Touch ID / PIN), the encrypted Cloud
 * Vault backup and the Decoy Vault are all REAL Private Photo Vault features; the
 * copy names them and claims nothing else. The cipher tiles are an illustration
 * of encrypted storage, not a literal render of the cipher text — copy says so.
 * No fake scarcity, no countdown, no data-loss-on-expiration threat (the "noise"
 * is what OTHERS see, never a claim that YOU lose your photos), no false
 * guarantees. "Save 79%" is real arithmetic from live product data. Yearly is the
 * featured, default, most prominent price; monthly is small & secondary. The
 * 7-day free trial is the real trial in the product data. Interior images are
 * labelled Unsplash demo photos, not the user's library.
 */

const COLS = 4
const ROWS = 3

// bundled Unsplash demo tiles (no network egress) — a full, varied library
const PHOTOS = [
  "/recall/sunset-1.jpg",
  "/recall/beach-1.jpg",
  "/recall/dog-1.jpg",
  "/recall/mountain-1.jpg",
  "/recall/coffee-1.jpg",
  "/recall/flower-1.jpg",
  "/recall/city-1.jpg",
  "/recall/food-1.jpg",
  "/recall/sunset-2.jpg",
  "/recall/beach-2.jpg",
  "/recall/mountain-2.jpg",
  "/recall/dog-2.jpg",
]

// three REAL, security-first Premium protections
const FEATURES = [
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private backup only your key can open",
  },
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID, Touch ID or a PIN you choose",
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Vault",
    line: "a second code opens a harmless fake",
  },
]

const GLYPHS = "0123456789ABCDEF{}<>/*#%$&+=?".split("")
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

const CipherPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // seam ∈ [0,1] — the fraction of the grid width the decryption key has swept.
  const [seam, setSeam] = useState(0)
  const gridRef = useRef<HTMLDivElement | null>(null)
  const dragging = useRef(false)
  const userActed = useRef(false)
  const rafId = useRef<number | null>(null)

  // per-tile cipher strips (deterministic within a mount, no re-render churn)
  const ciphers = useMemo(
    () =>
      PHOTOS.map(() =>
        Array.from({ length: 7 }, () =>
          Array.from(
            { length: 9 },
            () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)],
          ).join(" "),
        ),
      ),
    [],
  )

  const setSeamFromClientX = useCallback((clientX: number) => {
    const el = gridRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setSeam(clamp((clientX - r.left) / r.width, 0, 1))
  }, [])

  const stopRaf = useCallback(() => {
    if (rafId.current != null) {
      cancelAnimationFrame(rafId.current)
      rafId.current = null
    }
  }, [])

  // auto-demo on mount: sweep the key most of the way across so the mechanic —
  // and the payoff (noise resolving into real photos) — is legible at a glance.
  useEffect(() => {
    if (reduceMotion) {
      setSeam(0.64)
      return
    }
    const TARGET = 0.6
    const DUR = 1750
    let start: number | null = null
    const tick = (t: number) => {
      if (userActed.current) return
      if (start == null) start = t
      const p = clamp((t - start) / DUR, 0, 1)
      // easeInOutCubic
      const e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2
      setSeam(TARGET * e)
      if (p < 1) rafId.current = requestAnimationFrame(tick)
    }
    // small beat before the sweep begins
    rafId.current = requestAnimationFrame((t) => {
      start = t + 320
      rafId.current = requestAnimationFrame(tick)
    })
    return stopRaf
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      userActed.current = true
      stopRaf()
      dragging.current = true
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      setSeamFromClientX(e.clientX)
    },
    [setSeamFromClientX, stopRaf],
  )
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return
      setSeamFromClientX(e.clientX)
    },
    [setSeamFromClientX],
  )
  const onPointerUp = useCallback(() => {
    dragging.current = false
  }, [])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // how many tiles are currently decrypted (their column has been passed)
  const decryptedCount = PHOTOS.reduce((n, _, i) => {
    const col = i % COLS
    return n + (seam >= (col + 0.5) / COLS ? 1 : 0)
  }, 0)
  const allClear = decryptedCount >= PHOTOS.length
  const keyPct = Math.round(seam * 100)

  return (
    <div className={styles.root} style={{ ["--seam" as any]: seam }}>
      <div className={styles.grain} aria-hidden="true" />
      <div className={styles.glow} aria-hidden="true" />

      {/* header — brand clarity in the first glance */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* intro */}
      <section className={styles.intro}>
        <span className={styles.kicker}>
          <span className={styles.kickerDot} aria-hidden="true" />
          End-to-end encrypted
        </span>
        <h1 className={styles.title}>
          To everyone else,
          <br />
          <span className={styles.titleAccent}>it&rsquo;s just noise.</span>
        </h1>
        <p className={styles.sub}>
          Your vault keeps every photo encrypted. Without your key it&rsquo;s
          unreadable — so drag the key and watch the static turn back into your
          memories.
        </p>
      </section>

      {/* ── the decryption console ─────────────────────────────────────────── */}
      <section className={styles.console}>
        <div
          className={`${styles.grid} ${allClear ? styles.gridClear : ""}`}
          ref={gridRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          role="slider"
          aria-label="Drag your key to decrypt your photos"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={keyPct}
          style={{
            gridTemplateColumns: `repeat(${COLS}, 1fr)`,
            gridTemplateRows: `repeat(${ROWS}, 1fr)`,
          }}
        >
          {PHOTOS.map((src, i) => {
            const col = i % COLS
            const decrypted = seam >= (col + 0.5) / COLS
            return (
              <div
                key={src}
                className={`${styles.tile} ${decrypted ? styles.on : ""}`}
              >
                <img src={src} alt="" draggable={false} />
                <span className={styles.cipher} aria-hidden="true">
                  <span className={styles.cipherStream}>
                    {ciphers[i].map((line, li) => (
                      <span key={li}>{line}</span>
                    ))}
                  </span>
                </span>
                <span className={styles.lockDot} aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="9" height="9">
                    <rect
                      x="5"
                      y="10.5"
                      width="14"
                      height="9"
                      rx="2"
                      fill="currentColor"
                    />
                    <path
                      d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.9"
                    />
                  </svg>
                </span>
              </div>
            )
          })}

          {/* the bright decryption seam */}
          <div className={styles.seam} aria-hidden="true">
            <span className={styles.seamGlow} />
            <span className={styles.seamLine} />
          </div>
        </div>

        {/* the physical key that drives the seam */}
        <div
          className={styles.keybar}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className={styles.keyTrackLabel} data-side="left">
            Encrypted
          </span>
          <span className={styles.keyTrack} aria-hidden="true">
            <span className={styles.keyFill} />
            <span className={styles.keyThumb}>
              <svg viewBox="0 0 24 24" width="16" height="16">
                <circle
                  cx="8"
                  cy="8"
                  r="4.2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M11 11l8 8M16 16l2.5-2.5M14 14l2.5-2.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.keyTrackLabel} data-side="right">
            Yours
          </span>
        </div>

        <p className={styles.status} aria-live="polite">
          {allClear ? (
            <>
              <span className={styles.statusOk} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.6l4.3 4.3L19 7.4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              Readable by you — and no one else
            </>
          ) : (
            <>
              {decryptedCount} of {PHOTOS.length} decrypted · the rest stays
              noise to anyone without your key
            </>
          )}
        </p>
      </section>

      {/* three real, security-first protections */}
      <ul className={styles.features}>
        {FEATURES.map((f) => {
          const Icon = f.Icon
          return (
            <li key={f.key} className={styles.feature}>
              <span className={styles.featureIcon} aria-hidden="true">
                <Icon />
              </span>
              <span className={styles.featureText}>
                <span className={styles.featureName}>{f.name}</span>
                <span className={styles.featureLine}>{f.line}</span>
              </span>
            </li>
          )
        })}
      </ul>

      {/* honest, compliant reassurance */}
      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        The static is an illustration of encrypted storage. Encryption, your
        Face&nbsp;ID / Touch&nbsp;ID / PIN lock, the encrypted Cloud Vault and
        the Decoy Vault are real Private Photo Vault features. Your photos never
        leave your control.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ───────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${
            plan === "yearly" ? styles.selected : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Yearly · keep the key</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            7 days free, then about {yearlyPerMonth}/mo billed yearly
          </span>
        </button>

        <button
          className={`${styles.monthly} ${
            plan === "monthly" ? styles.monthlySel : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo photos from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Start free — then keep them yours"
              : "Start monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default CipherPaywall
